import { isFullyMatchedPath, strictRootSearchSchema, useEmbedMode } from "@blockdaemon/widget-host";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { normalizeFeatures } from "../features/customization/lib/config-schema";
import { getCachedWidgetConfig } from "../features/customization/utils/config-loader";
import { WalletProvider } from "../features/wallet/components/wallet-provider";
import { DevTools } from "../shared/components/dev-tools";
import { Widget } from "../shared/components/widget";
import { useDevToolsEnabled } from "../shared/utils/devtools-override";
import { getDefaultTab, TAB_TO_ROUTE, type TabName } from "../shared/utils/navigation-utils";

export const Route = createFileRoute("/_layout")({
  component: LayoutComponent,
  beforeLoad: ({ location, matches }) => {
    if (isFullyMatchedPath(location.pathname, matches)) {
      const rawSearch = location.searchStr;
      const strictSearchResult = strictRootSearchSchema.safeParse(
        Object.fromEntries(new URLSearchParams(rawSearch)),
      );

      if (!strictSearchResult.success) {
        throw strictSearchResult.error;
      }
    }

    const config = getCachedWidgetConfig();

    if (!config) {
      return;
    }

    const features = normalizeFeatures(config.features);

    const getFeatureFromPath = (pathname: string): TabName | null => {
      if (pathname.startsWith("/staking")) return "Staking";
      if (pathname.startsWith("/defi")) return "DeFi";
      if (pathname.startsWith("/swap")) return "Swap";
      return null;
    };

    const currentTab = getFeatureFromPath(location.pathname);

    if (location.pathname === "/") {
      const defaultTab = getDefaultTab(features);
      const redirectRoute = TAB_TO_ROUTE[defaultTab];
      throw redirect({ to: redirectRoute });
    }

    if (currentTab) {
      const featureKey = currentTab.toLowerCase() as keyof typeof features;

      if (!features[featureKey]) {
        const defaultTab = getDefaultTab(features);
        const redirectRoute = TAB_TO_ROUTE[defaultTab];
        throw redirect({ to: redirectRoute });
      }
    }
  },
});

const isWidgetOnlyBuild = import.meta.env.VITE_WIDGET_ONLY === "true";

function LayoutComponent() {
  const embed = useEmbedMode();
  const devToolsEnabled = useDevToolsEnabled();
  const widget = <Widget />;

  const wrapWithWallet = (node: ReactNode) => <WalletProvider>{node}</WalletProvider>;

  if ((isWidgetOnlyBuild || embed) && devToolsEnabled) {
    return wrapWithWallet(
      <>
        {widget}
        <DevTools />
      </>,
    );
  }

  if (isWidgetOnlyBuild || embed) {
    return wrapWithWallet(widget);
  }

  return wrapWithWallet(
    <>
      {widget}
      {devToolsEnabled ? <DevTools /> : null}
    </>,
  );
}
